const DESKTOP_QUERY = '(min-width: 768px)';

function setItemHidden(item, hidden) {
  if (hidden) {
    item.setAttribute('inert', '');
  } else {
    item.removeAttribute('inert');
  }
}

function revealAllItems(items) {
  items.forEach((item) => setItemHidden(item, false));
}

function isFullyVisible(track, item) {
  if (item.clientWidth <= 0) return false;
  const trackRect = track.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();
  const tolerance = 16;
  return itemRect.left >= trackRect.left - tolerance && itemRect.right <= trackRect.right + tolerance;
}

function visibleItems(track, items) {
  return Array.from(items).filter((item) => isFullyVisible(track, item));
}

function syncVisibleItems(track, items) {
  items.forEach((item) => setItemHidden(item, !isFullyVisible(track, item)));
}

function focusItem(item) {
  if (!item || item.hasAttribute('inert') || item.closest('[inert]')) return;

  const target = item.querySelector('a[href]') || item;
  if (target === item && !item.hasAttribute('tabindex')) {
    item.setAttribute('tabindex', '-1');
  }

  target.focus({ preventScroll: true });
}

function visibleText(el) {
  if (!el) return '';
  const clone = el.cloneNode(true);
  clone.querySelectorAll('style, script, noscript, svg, [aria-hidden="true"], .visually-hidden').forEach((node) => {
    node.remove();
  });
  return clone.textContent.replace(/\s+/g, ' ').trim();
}

function itemLabel(item) {
  const heading = item.querySelector('h1, h2, h3, h4, h5, h6');
  const headingText = visibleText(heading);
  if (headingText) return headingText;

  const img = item.querySelector('img[alt]');
  if (img?.alt?.trim()) return img.alt.trim();

  const labeledLink = item.querySelector('a[aria-label]');
  const ariaLabel = labeledLink?.getAttribute('aria-label')?.trim();
  if (ariaLabel) return ariaLabel;

  const overlayTitle = item.querySelector('.content__heading, .content_text');
  const overlayText = visibleText(overlayTitle);
  if (overlayText) return overlayText;

  return '';
}

function ensureLiveRegion(sliderComponent) {
  let region = sliderComponent.querySelector('[data-slider-live-region]');
  if (region) return region;

  region = document.createElement('div');
  region.className = 'visually-hidden';
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  region.setAttribute('data-slider-live-region', '');
  sliderComponent.appendChild(region);
  return region;
}

const SLIDER_SELECTOR = 'slider-component.desktop-slider--row-of-elements';

function bindRowSlider(sliderComponent) {
  if (sliderComponent.dataset.rowSliderBound === 'true') return;

  const track = sliderComponent.querySelector('.slider-component-slider');
  const items = sliderComponent.querySelectorAll('.slider-component-slider-item');
  const prevButton = sliderComponent.querySelector('button[name="previous"]');
  const nextButton = sliderComponent.querySelector('button[name="next"]');
  if (!track || !items.length) return;

  sliderComponent.dataset.rowSliderBound = 'true';

  const desktopMql = window.matchMedia(DESKTOP_QUERY);
  const liveRegion = ensureLiveRegion(sliderComponent);
  let frame = 0;
  let pendingMove = null;
  let focusTimer = 0;
  let lastControl = null;

  const controlIsHidden = (button) => {
    if (!button) return true;
    if (button.disabled || button.hasAttribute('disabled')) return true;
    const style = window.getComputedStyle(button);
    return style.visibility === 'hidden' || style.display === 'none';
  };

  const announce = (newlyVisible) => {
    const names = newlyVisible.map(itemLabel).filter(Boolean);
    if (!names.length) return;
    liveRegion.textContent = '';
    window.requestAnimationFrame(() => {
      liveRegion.textContent = names.join(', ');
    });
  };

  const restoreFocusIfControlHidden = (direction, newlyVisible, visible) => {
    if (!lastControl || !controlIsHidden(lastControl)) return;

    const target =
      direction === 'next'
        ? newlyVisible[newlyVisible.length - 1] || visible[visible.length - 1]
        : newlyVisible[0] || visible[0];
    window.clearTimeout(focusTimer);
    focusTimer = window.setTimeout(() => focusItem(target), 50);
  };

  const update = () => {
    if (!desktopMql.matches) {
      revealAllItems(items);
      return;
    }
    if (!Array.from(items).some((item) => item.clientWidth > 0)) return;
    syncVisibleItems(track, items);
  };

  const finishMove = (force = false) => {
    if (!pendingMove) {
      update();
      return;
    }

    update();

    const { direction, hiddenBefore } = pendingMove;
    const visible = visibleItems(track, items);
    const newlyVisible = visible.filter((item) => hiddenBefore.has(item));
    if (!newlyVisible.length && !force) return;

    pendingMove = null;
    if (lastControl && controlIsHidden(lastControl)) {
      restoreFocusIfControlHidden(direction, newlyVisible, visible);
    } else {
      announce(newlyVisible);
    }
  };

  const scheduleUpdate = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      if (pendingMove) {
        finishMove(false);
        return;
      }
      update();
    });
  };

  const onControlClick = (event, direction) => {
    lastControl = event.currentTarget;
    pendingMove = {
      direction,
      hiddenBefore: new Set(Array.from(items).filter((item) => item.hasAttribute('inert'))),
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => finishMove(false));
    });
    window.setTimeout(() => finishMove(true), 160);
  };

  prevButton?.addEventListener('click', (event) => onControlClick(event, 'prev'), true);
  nextButton?.addEventListener('click', (event) => onControlClick(event, 'next'), true);
  track.addEventListener('scroll', scheduleUpdate, { passive: true });
  track.addEventListener('scrollend', () => finishMove(false));
  window.addEventListener('resize', scheduleUpdate);
  desktopMql.addEventListener('change', scheduleUpdate);
  new ResizeObserver(scheduleUpdate).observe(track);
  update();
}

function bindRowSliders(root = document) {
  if (root.matches?.(SLIDER_SELECTOR)) bindRowSlider(root);
  root.querySelectorAll?.(SLIDER_SELECTOR).forEach(bindRowSlider);
}

bindRowSliders();

document.addEventListener('shopify:section:load', (event) => {
  bindRowSliders(event.target);
});
